import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCreateSoftware } from '@/hooks/useSoftwares';
import { useFabricantes } from '@/hooks/useFabricantes';
import { useFornecedores } from '@/hooks/useFornecedores';
import { useContratos } from '@/hooks/useContratos';
import { useAuth } from '@/hooks/useAuth';

const formSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  versao: z.string().optional(),
  descricao: z.string().optional(),
  fabricante_id: z.string().optional(),
  fornecedor_id: z.string().optional(),
  contrato_id: z.string().optional(),
  tipo_licenciamento: z.enum(['perpetua', 'assinatura', 'por_usuario', 'por_dispositivo', 'volume']),
  categoria: z.string().optional(),
  quantidade_contratada: z.coerce.number().min(1, 'Quantidade deve ser no mínimo 1'),
  valor_unitario: z.coerce.number().optional(),
  valor_total_contrato: z.coerce.number().optional(),
  data_aquisicao: z.string().optional(),
  data_vencimento: z.string().optional(),
  status: z.enum(['ativo', 'inativo', 'vencido', 'em_renovacao']),
});

type FormData = z.infer<typeof formSchema>;

interface NewSoftwareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewSoftwareDialog({ open, onOpenChange }: NewSoftwareDialogProps) {
  const createSoftware = useCreateSoftware();
  const { fabricantes } = useFabricantes();
  const { fornecedores } = useFornecedores();
  const { contratos } = useContratos();
  const { user } = useAuth();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nome: '',
      versao: '',
      descricao: '',
      tipo_licenciamento: 'perpetua',
      categoria: '',
      quantidade_contratada: 1,
      status: 'ativo',
    },
  });

  const onSubmit = async (data: FormData) => {
    await createSoftware.mutateAsync({
      nome: data.nome,
      tipo_licenciamento: data.tipo_licenciamento,
      quantidade_contratada: data.quantidade_contratada,
      status: data.status,
      versao: data.versao,
      descricao: data.descricao,
      categoria: data.categoria,
      fabricante_id: data.fabricante_id || undefined,
      fornecedor_id: data.fornecedor_id || undefined,
      contrato_id: data.contrato_id || undefined,
      valor_unitario: data.valor_unitario || undefined,
      valor_total_contrato: data.valor_total_contrato || undefined,
      data_aquisicao: data.data_aquisicao || undefined,
      data_vencimento: data.data_vencimento || undefined,
      // client_id e criado_por são definidos automaticamente pelo hook
    });
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Novo Software</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Tabs defaultValue="basico" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basico">Informações Básicas</TabsTrigger>
                <TabsTrigger value="licenciamento">Licenciamento</TabsTrigger>
                <TabsTrigger value="contrato">Contrato</TabsTrigger>
              </TabsList>

              <TabsContent value="basico" className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="nome"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome *</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Adobe Photoshop" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="versao"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Versão</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex: 2024" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="categoria"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Categoria</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex: Design, Produtividade" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="descricao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descrição</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Descrição do software..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="fabricante_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fabricante</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o fabricante" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {fabricantes?.map((fab) => (
                              <SelectItem key={fab.id} value={fab.id}>
                                {fab.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="ativo">Ativo</SelectItem>
                            <SelectItem value="inativo">Inativo</SelectItem>
                            <SelectItem value="vencido">Vencido</SelectItem>
                            <SelectItem value="em_renovacao">Em Renovação</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              <TabsContent value="licenciamento" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="tipo_licenciamento"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo de Licenciamento *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o tipo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="perpetua">Perpétua</SelectItem>
                            <SelectItem value="assinatura">Assinatura</SelectItem>
                            <SelectItem value="por_usuario">Por Usuário</SelectItem>
                            <SelectItem value="por_dispositivo">Por Dispositivo</SelectItem>
                            <SelectItem value="volume">Volume</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="quantidade_contratada"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Quantidade Contratada *</FormLabel>
                        <FormControl>
                          <Input type="number" min={1} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="valor_unitario"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Valor por Licença (R$)</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" placeholder="0,00" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="valor_total_contrato"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Valor Total do Contrato (R$)</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" placeholder="0,00" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              <TabsContent value="contrato" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="fornecedor_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fornecedor</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o fornecedor" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {fornecedores?.map((forn) => (
                              <SelectItem key={forn.id} value={forn.id}>
                                {forn.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contrato_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contrato Vinculado</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o contrato" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {contratos?.map((contrato) => (
                              <SelectItem key={contrato.id} value={contrato.id}>
                                {contrato.numero_contrato} - {contrato.nome_contrato}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="data_aquisicao"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Data de Aquisição</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="data_vencimento"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Data de Vencimento</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={createSoftware.isPending}>
                {createSoftware.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
